import type Purchases from 'react-native-purchases'

import type { UserEntitlement } from '../dal/user-dal'

type CustomerInfo = Awaited<ReturnType<typeof Purchases.getCustomerInfo>>

export function normalizeRevenueCatId(
  value: string | null | undefined,
): string | null {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function hasActiveAccess(customerInfo: CustomerInfo): boolean {
  const hasEntitlement =
    Object.keys(customerInfo.entitlements?.active ?? {}).length > 0
  if (hasEntitlement) return true
  return (customerInfo.activeSubscriptions?.length ?? 0) > 0
}

export function isRevenueCatOwnershipMatch({
  customerInfo,
  revenueCatId,
}: {
  customerInfo: CustomerInfo | null | undefined
  revenueCatId: string | null | undefined
}): boolean {
  if (customerInfo == null) {
    return false
  }

  const currentRevenueCatId = normalizeRevenueCatId(revenueCatId)
  const originalAppUserId = normalizeRevenueCatId(
    customerInfo.originalAppUserId,
  )

  return (
    currentRevenueCatId != null && originalAppUserId === currentRevenueCatId
  )
}

export function hasOwnedRevenueCatAccess({
  customerInfo,
  revenueCatId,
}: {
  customerInfo: CustomerInfo | null | undefined
  revenueCatId: string | null | undefined
}): boolean {
  return (
    customerInfo != null &&
    hasActiveAccess(customerInfo) &&
    isRevenueCatOwnershipMatch({ customerInfo, revenueCatId })
  )
}

export function hasBackendEntitlement(
  entitlement: UserEntitlement | null | undefined,
): boolean {
  if (entitlement == null) return false

  if (entitlement.expireDate == null) {
    return true
  }

  const expiry =
    entitlement.expireDate instanceof Date
      ? entitlement.expireDate
      : new Date(entitlement.expireDate)

  return Number.isFinite(expiry.getTime()) && expiry > new Date()
}

export function hasResolvedAccess({
  customerInfo,
  entitlement,
  revenueCatId,
}: {
  customerInfo: CustomerInfo | null | undefined
  entitlement: UserEntitlement | null | undefined
  revenueCatId: string | null | undefined
}): boolean {
  return (
    hasOwnedRevenueCatAccess({ customerInfo, revenueCatId }) ||
    hasBackendEntitlement(entitlement)
  )
}
